/**
 * Interceptor CSRF del checkout-proxy (S9 CU2b).
 *
 * El write-proxy (`ssr/checkout-proxy.ts`) exige un **double-submit**: la cookie
 * `cms_csrf_token` (no httpOnly) debe venir reflejada en el header `X-CSRF-Token`
 * en cada mutación. El interceptor XSRF built-in de Angular NO lo cubría de forma
 * confiable (aun con `withXsrfConfiguration`, el header no viajaba en prod), así que
 * este interceptor lo hace **explícito y determinista**: en el browser, para toda
 * mutación (POST/PUT/DELETE/PATCH) contra `CHECKOUT_PROXY_PATH`, resuelve la cookie
 * y setea el header. Fuera del browser (SSR) o para rutas ajenas al proxy no toca nada.
 *
 * **#354 — el bundle siembra la cookie si falta.** Antes la sembraba el SSR en TODA
 * respuesta, incluidas las páginas públicas cacheables; eso rompía el cache del edge
 * (ver `server.ts`). Ahora el SSR sólo la emite en superficies `no-store`, y la
 * navegación SPA (`/products` → `/cart` → `/checkout`, sin round-trip al server) se
 * cubre acá: si al momento de mutar no hay cookie válida, se genera una y se escribe.
 *
 * Que el token lo genere el cliente NO debilita el double-submit: el token nunca fue
 * secreto (es no-httpOnly a propósito, el bundle tiene que leerlo), ni firmado, ni
 * ligado a la sesión. Lo que protege es que un origen ajeno no puede leer ni escribir
 * la cookie de este origen, y por lo tanto no puede reflejarla como header. El server
 * sigue exigiendo `isTrustedOrigin` + match cookie↔header en tiempo constante. No
 * defiende de XSS same-origin — nunca lo hizo.
 */
import { HttpInterceptorFn } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CHECKOUT_PROXY_PATH } from '@core/cms-sdk';

const CSRF_COOKIE = 'cms_csrf_token';
const CSRF_HEADER = 'X-CSRF-Token';
const MUTATIONS = new Set(['POST', 'PUT', 'DELETE', 'PATCH']);

/** Formato del token que emite el SSR: `randomBytes(32).toString('hex')`. */
const TOKEN_FORMAT = /^[0-9a-f]{64}$/;

/**
 * Token sembrado por este documento. Hace idempotente la siembra ante mutaciones
 * concurrentes, sin depender del orden en que se resuelvan.
 */
let seededToken: string | null = null;

/**
 * Lee la cookie del `document.cookie` quedándose con la **última** coincidencia.
 * El `parseCookies` del server asigna en loop (gana la última); leer la primera
 * acá haría que, ante cookies duplicadas (una host-only y otra heredada de un
 * dominio padre), cliente y server compararan valores distintos → 403 eterno.
 */
function readCookie(name: string): string | null {
  const matches = document.cookie.matchAll(new RegExp(`(?:^|; )${name}=([^;]*)`, 'g'));
  let last: string | null = null;
  for (const match of matches) {
    try {
      last = decodeURIComponent(match[1]);
    } catch {
      // Un valor mal encodeado (un `%` suelto) hace tirar `URIError` a
      // `decodeURIComponent`. Se trata como cookie inválida para que el caller
      // la pise, en vez de reventar la mutación antes de poder autocurarse.
      last = null;
    }
  }
  return last;
}

/** Token hex de 256 bits, mismo formato y entropía que el `randomBytes(32)` del SSR. */
function generateToken(): string | null {
  const source = globalThis.crypto;
  if (!source?.getRandomValues) {
    return null;
  }
  const bytes = source.getRandomValues(new Uint8Array(32));
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Siembra la cookie con los MISMOS atributos que el SSR (`checkout-proxy.ts`):
 * `Path=/`, `SameSite=Strict`, `Secure` sólo bajo https, sin `Domain` y sin
 * expiración (cookie de sesión). Devuelve el token **sólo si el browser lo
 * persistió** — sin cookie del lado del server, mandar el header no sirve de nada
 * y ensucia la observabilidad del 403.
 */
function seedCookie(): string | null {
  const token = seededToken ?? generateToken();
  if (!token) {
    return null;
  }
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${CSRF_COOKIE}=${token}; Path=/; SameSite=Strict${secure}`;
  if (readCookie(CSRF_COOKIE) !== token) {
    return null;
  }
  seededToken = token;
  return token;
}

/**
 * Refleja la cookie CSRF del checkout-proxy en el header `X-CSRF-Token` para las
 * mutaciones same-origin contra el proxy (double-submit), sembrándola si falta o
 * si la existente está corrupta. No-op en SSR, en rutas fuera del proxy, en
 * métodos no mutantes o si el header ya está seteado.
 */
export const checkoutCsrfInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isPlatformBrowser(inject(PLATFORM_ID))) {
    return next(req);
  }
  if (!req.url.startsWith(CHECKOUT_PROXY_PATH) || !MUTATIONS.has(req.method.toUpperCase())) {
    return next(req);
  }
  if (req.headers.has(CSRF_HEADER)) {
    return next(req);
  }
  const current = readCookie(CSRF_COOKIE);
  // Una cookie vacía/truncada/corrupta se pisa: si no, condena al usuario a un 403
  // permanente en vez de autocurarse en la próxima mutación.
  const token = current && TOKEN_FORMAT.test(current) ? current : seedCookie();
  if (!token) {
    return next(req);
  }
  return next(req.clone({ headers: req.headers.set(CSRF_HEADER, token) }));
};
